/**
 * @zendir/ui - Custom icons
 *
 * Space/mission status glyphs that have no Lucide equivalent — Lucide ships
 * generic "satellite" and "antenna" marks, but not their transmit/receive/off
 * states. Built with Lucide's own `createLucideIcon` helper on the same base
 * shapes Lucide uses for "satellite" and "antenna", so these are drop-in
 * compatible with every real Lucide icon (same props, same ref forwarding,
 * same default size/stroke) and sit visually in the same family.
 *
 * @example
 * ```tsx
 * import { SatelliteTransmit, AntennaOff } from "@zendir/ui/react/icons";
 *
 * <SatelliteTransmit />
 * <AntennaOff size={20} />
 * ```
 */

import { createLucideIcon } from "lucide-react";
import type { IconNode } from "lucide-react";

// Shared base shapes, lifted from Lucide's own "satellite" / "antenna" icons
// so the composited variants below stay pixel-consistent with the originals.
const SATELLITE_DISH_ARMS: IconNode = [
  [
    "path",
    {
      d: "m13.5 6.5-3.148-3.148a1.205 1.205 0 0 0-1.704 0L6.352 5.648a1.205 1.205 0 0 0 0 1.704L9.5 10.5",
    },
  ],
  ["path", { d: "M16.5 7.5 19 5" }],
  [
    "path",
    {
      d: "m17.5 10.5 3.148 3.148a1.205 1.205 0 0 1 0 1.704l-2.296 2.296a1.205 1.205 0 0 1-1.704 0L13.5 14.5",
    },
  ],
];
const SATELLITE_BODY: IconNode = [
  [
    "path",
    {
      d: "M9.352 10.648a1.205 1.205 0 0 0 0 1.704l2.296 2.296a1.205 1.205 0 0 0 1.704 0l4.296-4.296a1.205 1.205 0 0 0 0-1.704l-2.296-2.296a1.205 1.205 0 0 0-1.704 0z",
    },
  ],
];
const SATELLITE_SIGNAL_ARC: IconNode = [["path", { d: "M9 21a6 6 0 0 0-6-6" }]];

const ANTENNA_BARS: IconNode = [
  ["path", { d: "M2 12 7 2" }],
  ["path", { d: "m7 12 5-10" }],
  ["path", { d: "m12 12 5-10" }],
  ["path", { d: "m17 12 5-10" }],
  ["path", { d: "M4.5 7h15" }],
  ["path", { d: "M12 16v6" }],
];

// Directional accents. Corner-arrow shapes replace or occupy genuinely empty
// space in the base glyph's bounding box — verified to add zero overlap with
// the kept base paths.
const ARROW_OUT_BOTTOM_LEFT: IconNode = [
  ["path", { d: "M2 22 8 16" }],
  ["path", { d: "M2 16v6h6" }],
];
const ARROW_IN_BOTTOM_LEFT: IconNode = [
  ["path", { d: "M8 22 2 16" }],
  ["path", { d: "M8 16v6H2" }],
];
const ARROW_OUT_BOTTOM_RIGHT: IconNode = [
  ["path", { d: "M22 22 16 16" }],
  ["path", { d: "M22 16v6h-6" }],
];
const ARROW_IN_BOTTOM_RIGHT: IconNode = [
  ["path", { d: "M16 22 22 16" }],
  ["path", { d: "M16 16v6h6" }],
];
const DIAGONAL_OFF: IconNode = [["path", { d: "m2 2 20 20" }]];

export const SatelliteTransmit = createLucideIcon("satellite-transmit", [
  ...SATELLITE_DISH_ARMS,
  ...SATELLITE_BODY,
  ...ARROW_OUT_BOTTOM_LEFT,
]);

export const SatelliteReceive = createLucideIcon("satellite-receive", [
  ...SATELLITE_DISH_ARMS,
  ...SATELLITE_BODY,
  ...ARROW_IN_BOTTOM_LEFT,
]);

export const SatelliteOff = createLucideIcon("satellite-off", [
  ...SATELLITE_DISH_ARMS,
  ...SATELLITE_BODY,
  ...SATELLITE_SIGNAL_ARC,
  ...DIAGONAL_OFF,
]);

export const AntennaTransmit = createLucideIcon("antenna-transmit", [
  ...ANTENNA_BARS,
  ...ARROW_OUT_BOTTOM_RIGHT,
]);

export const AntennaReceive = createLucideIcon("antenna-receive", [
  ...ANTENNA_BARS,
  ...ARROW_IN_BOTTOM_RIGHT,
]);

export const AntennaOff = createLucideIcon("antenna-off", [
  ...ANTENNA_BARS,
  ...DIAGONAL_OFF,
]);
